import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"
import styled from "styled-components"

import {
  useAssignReferralMutation,
  useGetAdminReferralQuery,
  useUpdateReferralClientDetailsMutation,
} from "store/api/referralsApi"
import { ApiTypes } from "types"

import getErrorMessage from "utils/getErrorMessage"
import { onFormValidate, referralClientDetailsSchema, validationInitialState } from "utils/onValidate"
import { ModalLayout } from "layouts/ModalLayout"
import { Spinner } from "components/common/Spinner"
import { StatusBadge, statusLabels } from "components/table/columnComponents/ReferralStatusCell"

import { AssignmentSection } from "./AssignmentSection"
import { CustomerDetailsSection } from "./CustomerDetailsSection"
import { getAssignmentValues, getClientValues, isSame, normalizeAssignment, normalizeClient } from "./formValues"

const StackedModalLayout = styled(ModalLayout)`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  > footer {
    flex-shrink: 0;
  }
`

const Header = styled.div`
  flex-shrink: 0;
  padding: 20px 56px 20px 20px;
  border-bottom: 1px solid var(--border-subtle);
`

const ScrollBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
`

const Subtitle = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--text-muted);
`

const LoadingContent = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`

interface FormProps {
  referral: ApiTypes.Model.Referral
  partnerName: string
  onCancel: () => void
  onSuccess: () => void
}

const EditReferralForm = ({ referral, partnerName, onCancel, onSuccess }: FormProps) => {
  const [clientValues, setClientValues] = useState(() => getClientValues(referral))
  const [assignmentValues, setAssignmentValues] = useState(() => getAssignmentValues(referral))
  const [savedClient, setSavedClient] = useState(() => normalizeClient(getClientValues(referral)))
  const [savedAssignment, setSavedAssignment] = useState(() => normalizeAssignment(getAssignmentValues(referral)))
  const [error, setError] = useState(validationInitialState)

  const [triggerAssignReferral, { isLoading: isAssigning }] = useAssignReferralMutation()
  const [triggerUpdateClientDetails, { isLoading: isUpdatingClient }] = useUpdateReferralClientDetailsMutation()
  const isSaving = isAssigning || isUpdatingClient

  const saveClientDetails = async () => {
    const res = await triggerUpdateClientDetails({
      id: referral.id,
      body: {
        homeowner_name: clientValues.homeowner_name.trim(),
        homeowner_email: clientValues.email.trim() || null,
        homeowner_phone: clientValues.phone.trim() || null,
        property_address1: clientValues.address1.trim() || null,
        property_address2: referral.property_address2,
        city_id: (clientValues.city[0]?.value as number) ?? null,
        state_id: (clientValues.state[0]?.value as number) ?? null,
        zip_id: referral.zip?.id ?? null,
        country_id: referral.country?.id ?? null,
      },
    })
    const saveError = getErrorMessage(res?.error)

    if (saveError) {
      toast.error(saveError)
      return false
    }

    setSavedClient(normalizeClient(clientValues))
    return true
  }

  const saveAssignment = async () => {
    const res = await triggerAssignReferral({
      id: referral.id,
      body: {
        csm_name: assignmentValues.csmName.trim() || undefined,
        contractor_id:
          assignmentValues.contractor[0]?.value != null ? Number(assignmentValues.contractor[0].value) : null,
        internal_note: assignmentValues.internalNote.trim() || undefined,
        thermostat_serial_numbers: assignmentValues.serialNumbers.map(serial => serial.trim()).filter(Boolean),
      },
    })
    const saveError = getErrorMessage(res?.error)

    if (saveError) {
      toast.error(saveError)
      return false
    }

    res.data?.data?.warnings?.forEach(warning => toast.warn(warning.message))
    setSavedAssignment(normalizeAssignment(assignmentValues))
    return true
  }

  const onSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault()
    if (isSaving) return

    const isClientChanged = !isSame(normalizeClient(clientValues), savedClient)
    const isAssignmentChanged = !isSame(normalizeAssignment(assignmentValues), savedAssignment)

    if (!isClientChanged && !isAssignmentChanged) {
      onSuccess()
      return
    }

    if (isClientChanged && !onFormValidate(clientValues, referralClientDetailsSchema, setError)) return

    try {
      if (isClientChanged && !(await saveClientDetails())) return
      if (isAssignmentChanged && !(await saveAssignment())) return

      toast.success("Referral has been successfully updated!")
      onSuccess()
    } catch (saveError) {
      console.error(saveError)
    }
  }

  const title = [referral.property_address1, referral.city?.name, referral.state?.name].filter(Boolean).join(", ")

  return (
    <StackedModalLayout
      width="640px"
      confirmButtonText="Save"
      cancelButtonText="Close"
      confirmButtonType="submit"
      isFetching={isSaving}
      onConfirm={onSubmit}
      onCancel={onCancel}
    >
      <Header>
        <Title>{title}</Title>
        <Subtitle>
          {`${referral.homeowner_name} · Placed ${dayjs(referral.created_at).format("MMM D")} · Realtor: ${partnerName}`}
          <StatusBadge $status={referral.status}>{statusLabels[referral.status] ?? referral.status}</StatusBadge>
        </Subtitle>
      </Header>
      <ScrollBody>
        <AssignmentSection values={assignmentValues} setValues={setAssignmentValues} />
        <CustomerDetailsSection values={clientValues} error={error} setValues={setClientValues} />
      </ScrollBody>
    </StackedModalLayout>
  )
}

interface Props {
  referralId: number
  partnerName: string
  onCancel: () => void
  onSuccess: () => void
}

export const EditReferralModal = ({ referralId, partnerName, onCancel, onSuccess }: Props) => {
  const { data, isFetching, isError } = useGetAdminReferralQuery(referralId, { refetchOnMountOrArgChange: true })
  const [referral, setReferral] = useState<ApiTypes.Model.Referral>()

  useEffect(() => {
    if (!referral && !isFetching && data?.data) setReferral(data.data)
  }, [referral, isFetching, data])

  useEffect(() => {
    if (!isError) return
    toast.error("Couldn't load the referral. Please try again.")
    onCancel()
  }, [isError])

  return createPortal(
    !referral ? (
      <ModalLayout width="640px" isWithoutFooter onCancel={onCancel}>
        <LoadingContent>
          <Spinner />
        </LoadingContent>
      </ModalLayout>
    ) : (
      <EditReferralForm referral={referral} partnerName={partnerName} onCancel={onCancel} onSuccess={onSuccess} />
    ),
    document.body,
  )
}
